import { EditCardTabsLeftBarDiv } from '../../../../../../../baseComponents/EditCardTabs'
import { ComponentTabObject, useTabObjectLogic } from '../../../../../../../utils/hooks/useTabLogic'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import CPTSectionEdit from './Tabs/CPTSectionEdit'
import { ButtonPrimary, ButtonSecondary } from '../../../../../../../baseComponents/Button'
import TwinTrans from '../../../../../../../baseComponents/TwinTrans'
import { PdfConfigAllModules, PdfConfigPage, PdfConfigSection, PdfConfigTemplateSingleTy } from '../types'
import { ComponentWithPermissions} from '../../../../../../../baseComponents/CreateRoutering/types'
import { useCallback } from 'react'
import twinFetchPost, { twinFetchPostJSON, twinFetchPostPrinter } from '../../../../../../../utils/globals/data'
import { faCog, faCopy, faPlus } from '@fortawesome/pro-light-svg-icons'
import TwinIcon from '../../../../../../../baseComponents/TwinIcon'
import useEditDeleteModal from '../../../../../../../utils/hooks/useEditDeleteModal'
import NullComponent from '../../../../../../../baseComponents/NullComponent'
import { nanoid } from 'nanoid'
import { TwinDictionary } from '../../../../../../../utils/globals/dictionary'
import { addAntiUnsaveElement, changeSuccessMessage, deleteAntiUnsaveElement } from '../../../../../../../utils/reducers/reduxDispatch'
import ModalConfigPdfEditSectionStyles from './ModalConfigPdfEditSectionStyles'
import useIsLoading from '../../../../../../../utils/hooks/useIsLoading'
import LoadingSpinner from '../../../../../../../baseComponents/LoaderDecider/LoadingSpinner'
import ModalConfigPdfEditDocumentStyles from './ModalConfigPdfEditDocumentStyles'
import ModalConfigPdfEditPageStyles from './ModalConfigPdfEditPageStyles'
import { arrayMove } from '@dnd-kit/sortable'
import ConfigPdfTemplateChangePosIcons, { ConfigPdfTemplateChangePosIconsProps } from './ConfigPdfTemplateChangePosIcons'

type ConfigPdfTemplateConfigProps = ComponentWithPermissions & ConfigPdfTemplateConfigLogicProps & {
}

const ConfigPdfTemplateConfig: React.FC<ConfigPdfTemplateConfigProps> = ({ config, ...logic }) => {
    const { saveData, downloadPdf, setOpenModal, openModal, addSection, addPage, loading } = useConfigPdfTemplateConfigLogic({ config, ...logic })
    const { t } = useTwinTranslation()
    const middleTabs: ComponentTabObject = {}
    const dictByKeys: TwinDictionary = {}
    for (const key in config.pages) {
        const page = config.pages[key]
        dictByKeys[page.id] = key 
        middleTabs['page' + page.id] = { component: PageExplain, text: <TabPage text={page.name} onClick={() => setOpenModal({ type: 'edit', allRowData: { 'id': 'page' + key,} })} setConfig={logic.setConfig} elementKey={key}/> }
        for (const keySect in page.sections) {
            const section = page.sections[keySect]
            dictByKeys[section.id] = key + ',' + keySect
            middleTabs[section.id] = { component: CPTSectionEdit, text: <TabSection text={section.name} onClick={() => setOpenModal({ type: 'edit', allRowData: { 'id': key + ',' + keySect,} })} setConfig={logic.setConfig} elementKey={dictByKeys[section.id]} /> }
        }
        middleTabs['addElement' + page.id] = { component: NullComponent, text: <AddElement onClick={() => addSection(parseInt(key))} /> }
    }
    const tabs: ComponentTabObject = {
        'document': { component: DocumentExplain, text: <TabDocument text={t('document', 'Documento')} onClick={() => setOpenModal({ type: 'edit', allRowData: { 'id': 'document' } })} /> },
        'addPage': { component: NullComponent, text: <AddPage onClick={addPage} />},
        ...middleTabs,
    }
    const { active, ComponentFromTab, setActive } = useTabObjectLogic(tabs, 'document')
    const Component = ModalConfigPdfEditDecider(openModal?.allRowData?.id || '')
    return (
        <div className='left-side flex'>
            <EditCardTabsLeftBarDiv tabs={tabs} tab={active} onClick={setActive}>
                <div className='mt-auto pr-15'>
                    {loading
                        ? <div className='w-full mb-20'><LoadingSpinner className='h-24' /></div>
                        : <ButtonSecondary className='mb-10 w-full' onClick={downloadPdf}>
                            <TwinTrans transKey='downloadPdf' >Descargar PDF</TwinTrans>
                            <a className='hidden downloadfile' download={true} href='/' target='_blank' onClick={(e) => e.stopPropagation()}>a</a>
                        </ButtonSecondary>
                    }
                    <ButtonPrimary className='w-full' onClick={saveData}>
                        <TwinTrans transKey='save' >Guardar</TwinTrans>
                    </ButtonPrimary>
                </div>
            </EditCardTabsLeftBarDiv>
            <div className='left-side-inner'>
                <ComponentFromTab {...{ config, ...logic, sectionKey: dictByKeys[active] }} />
            </div>
            {openModal ? <Component {...{ openModal, setOpenModal, config, ...logic }} /> : null}
        </div>
    )
}

interface ConfigPdfTemplateConfigLogicProps {
    config: PdfConfigTemplateSingleTy
    setConfig: React.Dispatch<React.SetStateAction<PdfConfigTemplateSingleTy>>
}

const useConfigPdfTemplateConfigLogic = ({ config, setConfig }: ConfigPdfTemplateConfigLogicProps) => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    const { startLoading, endLoading, loading } = useIsLoading()
    const { t } = useTwinTranslation()
    const pdfExampleString = t('pdfExampleHyphen', 'Ejemplo-Pdf')
    const message = t('successfullyCompleted', 'Realizado correctamente')
    const errorMessage = t('couldNotConnectWithPrinter', 'No se ha podido conectar con la impresora')

    const saveData = useCallback(async () => {
        const copyConfig = JSON.parse(JSON.stringify(config))
        if (copyConfig.document.cssConfig.backgroundImageParsed) {
            delete copyConfig.document.cssConfig.backgroundImageParsed
        }
        for (const element of copyConfig.pages) {
            if (element.cssConfig.backgroundImageParsed) {
                delete element.cssConfig.backgroundImageParsed
            }
        }
        await twinFetchPostJSON('/api/app/config/pdfTemplate/updatePdfTemplate', {
            id: config.id,
            code: JSON.stringify({ ...copyConfig })
        })
        await deleteAntiUnsaveElement('pdf_template_changes')
        changeSuccessMessage(message)
    }, [config, message])

    const addPage = useCallback(async () => {
        await addAntiUnsaveElement(['pdf_template_changes', '{}'])
        setConfig((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            const page: PdfConfigPage = {
                name: t('page', 'Página') + ' ' + ((config.pages?.length || 0) + 1),
                id: copy.pages.length + nanoid(),
                sections: [],
                cssConfig: {}
            }
            if (!copy.pages || copy.pages.length === 0) {
                copy.pages = []
            }
            copy.pages.push(page)
            return {...copy}
        })
    }, [setConfig, t, config])

    const addSection = useCallback(async (keyPage: number) => {
        await addAntiUnsaveElement(['pdf_template_changes', '{}'])
        setConfig((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            const sections = copy.pages[keyPage].sections
            const element: PdfConfigSection = {
                name: t('section', 'Sección') + ' ' + (config.pages[keyPage].sections.length + 1),
                id: sections.length + nanoid(),
                elements: [],
                cssConfig: {}
            }
            sections.push(element)
            return {...copy}
        })
    }, [setConfig, t, config])
    
    const downloadPdf = useCallback(async (e: React.MouseEvent) => {
        startLoading()
        const current = e.currentTarget
        const result = await twinFetchPost('/api/app/config/pdfTemplate/getExamplePdfByType ', {code: config })
        if (result && result.status === 200) {
            const pdf = await result.blob()
            const fileURL = URL.createObjectURL(pdf)
            const a = current.querySelector('a')
            const fileName = `${pdfExampleString}-${config.id}`
            if (a) {
                a.href = fileURL
                a.download = fileName + '.pdf'
                a.click()
            }
            endLoading()
        }else if(result && result.status === 367){
            const body = await result.json()
            await twinFetchPostPrinter('/print', body, errorMessage)
            endLoading()
        }
    }, [pdfExampleString, startLoading, endLoading, config, errorMessage])

    return { saveData, downloadPdf, openModal, setOpenModal, addSection, addPage, loading }
}

const ModalConfigPdfEditDecider = (id: string) => {
    if (id === 'document') {
        return ModalConfigPdfEditDocumentStyles
    } else if (id.includes('page')) {
        return ModalConfigPdfEditPageStyles
    }
    return ModalConfigPdfEditSectionStyles
}
interface DocumentExplainProps {}

const DocumentExplain: React.FC<DocumentExplainProps> = () => {
    return (
        <div>
            <TwinTrans transKey='documentEditExplanation'>Desde aquí podrás editar tu documento. Puedes ajustar tus estilos generales en la rueda de configuración al lado de documento.</TwinTrans>
        </div>
    )
}
interface PageExplainProps {}

const PageExplain: React.FC<PageExplainProps> = () => {
    return (
        <div>
            <TwinTrans transKey='pageEditExplanation'>Desde aquí podrás editar tu página. Puedes ajustar los estilos de la página en la rueda de configuración.</TwinTrans>
        </div>
    )
}
interface TabDocumentProps {
    text: string
    onClick: () => void
}

const TabDocument: React.FC<TabDocumentProps> = ({ text, onClick }) => {
    return (
        <div className='flex items-center w-full'>
            <span className='twin_elipsis configPdf_tab_element'>{text}</span>
            <TwinIcon icon={faCog} className='ml-auto p-3 cursor-pointer' onClick={onClick} />
        </div>
    )
}

interface TabPageProps extends TabPageLogicProps {
    text: string
    onClick: () => void
}


const TabPage: React.FC<TabPageProps> = ({ elementKey, setConfig, ...rest }) => {
    const { ...logic } = useTabPageLogic({ elementKey, setConfig })
    return (
        <TabBase {...rest} {...logic} />
    )
}

interface TabPageLogicProps {
    elementKey: string
    setConfig: React.Dispatch<React.SetStateAction<PdfConfigTemplateSingleTy>>
}

const useTabPageLogic = ({ setConfig, elementKey }: TabPageLogicProps) => {
    const {t} = useTwinTranslation()

    const pageText = t('page', 'Página')
    const handleChangePositionUp = useCallback(() => {
        setConfig((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            const myPosition = parseInt(elementKey)
            const toIndex = myPosition - 1 < 0 ? myPosition : myPosition - 1
            const newPages = arrayMove(copy.pages, myPosition, toIndex)
            copy.pages = newPages
            return { ...copy }
        })
    }, [setConfig, elementKey])

    const handleChangePositionDown = useCallback(() => {
        setConfig((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            const myPosition = parseInt(elementKey)
            const lastPosition = copy.pages.length - 1
            const toIndex = myPosition + 1 > lastPosition ? myPosition : myPosition + 1
            const newPages = arrayMove(copy.pages, myPosition, toIndex)
            copy.pages = newPages
            return { ...copy }
        })
    }, [setConfig, elementKey])
    
    const handleDuplicate = useCallback(() => {
        setConfig((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            const myPosition = parseInt(elementKey)
            const myNewSections: PdfConfigSection[] = []
            const sections = copy.pages[myPosition].sections
            for (const key in sections) {
                const pos = parseInt(key)
                const mySection = sections[pos]
                const myElements: PdfConfigAllModules[] = []
                if(mySection.elements?.length){
                    for (const x in mySection.elements) {
                        const posElement = parseInt(x)
                        const myElement = mySection.elements[posElement]
                        myElements.push({ ...myElement, id: pos + nanoid() })
                    }
                }
                myNewSections.push({ ...mySection, id: pos +nanoid(), elements: myElements})
            }
            const myDuplicatePage: PdfConfigPage = {
                ...copy.pages[myPosition],
                sections: myNewSections,
                name: pageText + ' ' + ((copy.pages?.length || 0) + 1),
                id: copy.pages.length + nanoid(),
            }
            copy.pages.splice(myPosition + 1, 0, myDuplicatePage)
            return { ...copy }
        })
    }, [setConfig, elementKey, pageText])

    return { handleChangePositionUp, handleChangePositionDown, handleDuplicate }
}

interface TabSectionProps extends TabSectionLogicProps {
    text: string
    onClick: () => void
}

const TabSection: React.FC<TabSectionProps> = ({ elementKey, setConfig, ...rest }) => {
    const { ...logic } = useTabSectionLogic({ elementKey, setConfig })
    return (
        <TabBase className='pl-20' {...rest} {...logic} />
    )
}

interface TabSectionLogicProps {
    elementKey: string
    setConfig: React.Dispatch<React.SetStateAction<PdfConfigTemplateSingleTy>>
}

const useTabSectionLogic = ({ setConfig, elementKey }: TabSectionLogicProps) => {
    const {t} = useTwinTranslation()

    const sectionText = t('section', 'Sección')
    const handleChangePositionUp = useCallback(() => {
        setConfig((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            const myPositions = elementKey.split(',')
            const sectionPosition = parseInt(myPositions[1])
            const toIndex = sectionPosition - 1 < 0 ? sectionPosition : sectionPosition - 1
            const newSection = arrayMove(copy.pages[myPositions[0]].sections, sectionPosition, toIndex)
            copy.pages[myPositions[0]].sections = newSection
            return { ...copy }
        })
    }, [setConfig, elementKey])

    const handleChangePositionDown = useCallback(() => {
        setConfig((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            const myPositions = elementKey.split(',')
            const sectionPosition = parseInt(myPositions[1])
            const lastPosition = copy.pages[myPositions[0]].sections.length - 1
            const toIndex = sectionPosition + 1 > lastPosition ? sectionPosition : sectionPosition + 1
            const newSection = arrayMove(copy.pages[myPositions[0]].sections, sectionPosition, toIndex)
            copy.pages[myPositions[0]].sections = newSection
            return { ...copy }
        })
    }, [setConfig, elementKey])

    const handleDuplicate = useCallback(() => {
        setConfig((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            const myPositions = elementKey.split(',')
            const sectionPosition = parseInt(myPositions[1])
            const myElements: PdfConfigAllModules[] = []
            const mySection = copy.pages[myPositions[0]].sections[sectionPosition]
            for (const key in mySection.elements) {
                const pos = parseInt(key)
                const element = mySection.elements[pos]
                myElements.push({...element, id: pos + nanoid()})
            }
            const myDuplicateElement: PdfConfigSection = {
                ...mySection,
                elements: myElements,
                name: sectionText + ' ' + (copy.pages[myPositions[0]].sections.length + 1),
                id: copy.pages[myPositions[0]].sections.length + nanoid(),
            }
            copy.pages[myPositions[0]].sections.splice(sectionPosition + 1, 0, myDuplicateElement)
            return { ...copy }
        })
    }, [setConfig, elementKey, sectionText])
    return { handleChangePositionUp, handleChangePositionDown, handleDuplicate }
}

interface TabBaseProps extends ConfigPdfTemplateChangePosIconsProps{
    text: string
    onClick: () => void
    handleDuplicate: ()=>void
    className?: string
}

const TabBase: React.FC<TabBaseProps> = ({className, handleChangePositionDown, handleChangePositionUp, handleDuplicate,  onClick, text}) => {
    return (
        <div className={'flex items-center w-full ' + (className || '')}>
            <span className='twin_elipsis configPdf_tab_element'>{text}</span>
            <TwinIcon icon={faCog} className='ml-auto p-3 cursor-pointer' onClick={onClick} />
            <ConfigPdfTemplateChangePosIcons handleChangePositionDown={handleChangePositionDown} handleChangePositionUp={handleChangePositionUp} />
            <TwinIcon icon={faCopy} className='ml-5 p-3 cursor-pointer' onClick={handleDuplicate} />
        </div> 
    )
}

interface AddElementProps {
    onClick: () => void
}

const AddElement: React.FC<AddElementProps> = ({ onClick }) => {
    return (
        <div className='flex items-center justify-center w-full' onClick={(e) => { e.stopPropagation(); onClick()}}>< TwinIcon icon={faPlus} className='p-3 cursor-pointer mr-5 mb-1'  /><span><TwinTrans transKey='addSection'>Añadir sección</TwinTrans></span></div> 
    )
}
interface AddPageProps {
    onClick: () => void
}

const AddPage: React.FC<AddPageProps> = ({onClick}) => {
    return (
        <div className='flex items-center justify-center w-full' onClick={(e) => {e.stopPropagation();  onClick()}}>< TwinIcon icon={faPlus} className='p-3 cursor-pointer mr-5 mb-1'  /><span><TwinTrans transKey='addPage'>Añadir página</TwinTrans></span></div> 
    )
}

export default ConfigPdfTemplateConfig
